import { useState } from "react";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CodeCard from "@/components/CodeCard";
import { Wand2, Globe, ImageOff } from "lucide-react";
import { toast } from "sonner";

const T = {
  en: {
    title: "Meta Tags Generator",
    sub: "Fill in your page details, watch the live previews, then copy the complete HTML head block.",
    details: "Page details",
    fTitle: "Title",
    fTitlePh: "Your Page Title - Brand",
    titleHint: "30-60 characters is ideal",
    fDesc: "Description",
    fDescPh: "A concise, compelling summary of this page...",
    descHint: "120-160 characters is ideal",
    fUrl: "Page URL (canonical)",
    fSite: "Site name",
    fAuthor: "Author",
    fImage: "Social image URL",
    imageHint: "Recommended: 1200 x 630px",
    fOgType: "OG type",
    fTwCard: "Twitter card",
    fTwHandle: "X / Twitter handle",
    fRobots: "Robots directive",
    generate: "Generate HTML",
    generating: "Generating...",
    needInput: "Add at least a title or description first",
    generated: "Meta tags generated",
    previews: "Live previews",
    serpTitle: "Your page title will appear here",
    serpDesc: "Your meta description will appear here. Keep it concise and compelling to earn the click.",
    ogTitle: "Your page title",
    ogDesc: "Your description appears here.",
    outputTitle: "Generated meta tags",
    imgFail: "Image failed to load",
    imgAdd: "Add a social image URL (1200 x 630)",
    tooLong: "Too long",
    good: "Good",
  },
  fr: {
    title: "Générateur de balises méta",
    sub: "Remplissez les détails de votre page, regardez les aperçus en direct, puis copiez le bloc HTML complet.",
    details: "Détails de la page",
    fTitle: "Titre",
    fTitlePh: "Titre de votre page - Marque",
    titleHint: "30 à 60 caractères, c'est l'idéal",
    fDesc: "Description",
    fDescPh: "Un résumé concis et accrocheur de cette page...",
    descHint: "120 à 160 caractères, c'est l'idéal",
    fUrl: "URL de la page (canonique)",
    fSite: "Nom du site",
    fAuthor: "Auteur",
    fImage: "URL de l'image sociale",
    imageHint: "Recommandé : 1200 x 630 px",
    fOgType: "Type OG",
    fTwCard: "Carte Twitter",
    fTwHandle: "Compte X / Twitter",
    fRobots: "Directive robots",
    generate: "Générer le HTML",
    generating: "Génération...",
    needInput: "Ajoutez au moins un titre ou une description",
    generated: "Balises méta générées",
    previews: "Aperçus en direct",
    serpTitle: "Le titre de votre page apparaîtra ici",
    serpDesc: "Votre méta description apparaîtra ici. Gardez-la concise et accrocheuse pour gagner le clic.",
    ogTitle: "Titre de votre page",
    ogDesc: "Votre description apparaît ici.",
    outputTitle: "Balises méta générées",
    imgFail: "L'image n'a pas pu charger",
    imgAdd: "Ajoutez une URL d'image sociale (1200 x 630)",
    tooLong: "Trop long",
    good: "Bon",
  },
};

export default function MetaGenerator() {
  const { lang } = useLang();
  const t = T[lang];
  const [form, setForm] = useState({
    title: "", description: "", canonical: "", siteName: "", image: "",
    ogType: "website", twitterCard: "summary_large_image", twitterSite: "", author: "", robots: "index, follow",
  });
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);

  const counterBadge = (len, max) => {
    if (len === 0) return null;
    const over = len > max;
    return (
      <Badge variant="outline" className={`text-[10px] tabular-nums ${over ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
        {len}/{max} {over ? t.tooLong : t.good}
      </Badge>
    );
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e?.target ? e.target.value : e });

  const generate = async () => {
    if (!form.title.trim() && !form.description.trim()) {
      toast.error(t.needInput);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/generate/meta", form);
      setHtml(data.html);
      toast.success(t.generated);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const displayUrl = form.canonical || "https://example.com/page";
  let displayDomain = "example.com";
  try {
    displayDomain = new URL(displayUrl.startsWith("http") ? displayUrl : `https://${displayUrl}`).hostname;
  } catch { /* keep default */ }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <Card className="lg:col-span-5 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">{t.details}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="mg-title">{t.fTitle}</Label>
                {counterBadge(form.title.length, 60)}
              </div>
              <Input id="mg-title" value={form.title} onChange={set("title")} placeholder={t.fTitlePh} data-testid="meta-generator-title-input" />
              <p className="text-xs text-muted-foreground">{t.titleHint}</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="mg-desc">{t.fDesc}</Label>
                {counterBadge(form.description.length, 160)}
              </div>
              <Textarea id="mg-desc" value={form.description} onChange={set("description")} rows={3} placeholder={t.fDescPh} data-testid="meta-generator-description-input" />
              <p className="text-xs text-muted-foreground">{t.descHint}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mg-url">{t.fUrl}</Label>
              <Input id="mg-url" value={form.canonical} onChange={set("canonical")} placeholder="https://exemple.com/page" data-testid="meta-generator-canonical-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="mg-site">{t.fSite}</Label>
                <Input id="mg-site" value={form.siteName} onChange={set("siteName")} placeholder="Ma Marque" data-testid="meta-generator-sitename-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mg-author">{t.fAuthor}</Label>
                <Input id="mg-author" value={form.author} onChange={set("author")} placeholder="Jeanne Tremblay" data-testid="meta-generator-author-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mg-image">{t.fImage}</Label>
              <Input id="mg-image" value={form.image} onChange={set("image")} placeholder="https://exemple.com/og-image.jpg" data-testid="meta-generator-image-input" />
              <p className="text-xs text-muted-foreground">{t.imageHint}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.fOgType}</Label>
                <Select value={form.ogType} onValueChange={set("ogType")}>
                  <SelectTrigger data-testid="meta-generator-ogtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">website</SelectItem>
                    <SelectItem value="article">article</SelectItem>
                    <SelectItem value="product">product</SelectItem>
                    <SelectItem value="profile">profile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t.fTwCard}</Label>
                <Select value={form.twitterCard} onValueChange={set("twitterCard")}>
                  <SelectTrigger data-testid="meta-generator-twittercard-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary_large_image">summary_large_image</SelectItem>
                    <SelectItem value="summary">summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="mg-twsite">{t.fTwHandle}</Label>
                <Input id="mg-twsite" value={form.twitterSite} onChange={set("twitterSite")} placeholder="@mamarque" data-testid="meta-generator-twittersite-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t.fRobots}</Label>
                <Select value={form.robots} onValueChange={set("robots")}>
                  <SelectTrigger data-testid="meta-generator-robots-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="index, follow">index, follow</SelectItem>
                    <SelectItem value="noindex, follow">noindex, follow</SelectItem>
                    <SelectItem value="index, nofollow">index, nofollow</SelectItem>
                    <SelectItem value="noindex, nofollow">noindex, nofollow</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={generate} disabled={loading} className="w-full" data-testid="meta-generator-generate-button">
              <Wand2 className="h-4 w-4 mr-2" />
              {loading ? t.generating : t.generate}
            </Button>
          </CardContent>
        </Card>

        {/* Previews */}
        <div className="lg:col-span-7 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">{t.previews}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="serp">
                <TabsList>
                  <TabsTrigger value="serp" data-testid="meta-generator-tab-serp">Google</TabsTrigger>
                  <TabsTrigger value="og" data-testid="meta-generator-tab-og">Facebook</TabsTrigger>
                  <TabsTrigger value="twitter" data-testid="meta-generator-tab-twitter">X / Twitter</TabsTrigger>
                </TabsList>

                <TabsContent value="serp" className="mt-4">
                  <div className="rounded-lg border bg-white p-5" data-testid="meta-generator-serp-preview">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted border">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <div className="leading-tight">
                        <p className="text-[13px] text-[#202124]">{form.siteName || displayDomain}</p>
                        <p className="text-xs text-[#4d5156]">{displayUrl}</p>
                      </div>
                    </div>
                    <p className="mt-2 text-xl text-[#1a0dab] leading-snug line-clamp-1 hover:underline cursor-pointer">
                      {form.title || t.serpTitle}
                    </p>
                    <p className="mt-1 text-sm text-[#4d5156] line-clamp-2">
                      {form.description || t.serpDesc}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="og" className="mt-4">
                  <div className="rounded-lg border overflow-hidden bg-white max-w-lg" data-testid="meta-generator-og-preview">
                    <SocialImage image={form.image} t={t} />
                    <div className="px-4 py-3 bg-[#f2f3f5]">
                      <p className="text-xs uppercase text-[#606770]">{displayDomain}</p>
                      <p className="text-[15px] font-semibold text-[#1d2129] line-clamp-1 mt-0.5">{form.title || t.ogTitle}</p>
                      <p className="text-[13px] text-[#606770] line-clamp-2 mt-0.5">{form.description || t.ogDesc}</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="twitter" className="mt-4">
                  <div className="rounded-2xl border overflow-hidden bg-white max-w-lg" data-testid="meta-generator-twitter-preview">
                    <SocialImage image={form.image} t={t} />
                    <div className="px-4 py-3">
                      <p className="text-xs text-[#536471]">{displayDomain}</p>
                      <p className="text-[15px] text-[#0f1419] line-clamp-1 mt-0.5">{form.title || t.ogTitle}</p>
                      <p className="text-[13px] text-[#536471] line-clamp-2 mt-0.5">{form.description || t.ogDesc}</p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {html && (
            <CodeCard
              title={t.outputTitle}
              language="html"
              code={html}
              filename="meta-tags.html"
              testIdPrefix="meta-generator-html"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const SocialImage = ({ image, t }) => {
  const [broken, setBroken] = useState(false);
  if (image && !broken) {
    return <img src={image} alt="Social preview" className="w-full aspect-[1.91/1] object-cover" onError={() => setBroken(true)} />;
  }
  return (
    <div className="w-full aspect-[1.91/1] bg-muted flex flex-col items-center justify-center gap-2">
      <ImageOff className="h-6 w-6 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{image ? t.imgFail : t.imgAdd}</span>
    </div>
  );
};
